import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Button } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { useTrack } from "pro-features/components/WithTrack/src/useTrack";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { EXTENSION_LINK } from "app/pages/sales-intelligence/constants/common";
import { setIsExtensionBannerVisible } from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import { selectIsExtensionBannerVisible } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { isSalesExtensionPresent } from "app/pages/sales-intelligence/utils/extension";
import { AssetsService } from "app/services/AssetsService";

import {
    StyledExtensionBanner,
    StyledExtensionBannerCloseButton,
    StyledExtensionBannerImageBlock,
    StyledExtensionBannerTextBlock,
    StyledExtensionBannerTitle,
    StyledExtensionBannerDescription,
    StyledExtensionBannerButtonContainer,
    StyledExtensionBannerOverlay,
    StyledExtensionBannerImage,
} from "./styles";

const EXTENSION_BANNER_KEY = "si:extension-banner";

export const isExtensionBannerRendered = () => {
    const hasExtension = isSalesExtensionPresent();
    const wasExtensionBannerClosed = localStorage.getItem(EXTENSION_BANNER_KEY);
    return !hasExtension && !wasExtensionBannerClosed;
};

export const ExtensionBanner = () => {
    const translate = useTranslation();
    const [track] = useTrack();
    const dispatch = useDispatch();
    const isBannerVisible = useSelector(selectIsExtensionBannerVisible);
    const isBannerVisibleClaim = swSettings.components.SalesWorkspace.resources.SiExtensionBannerVisible ?? true;

    const [isBannerOpen, setIsBannerOpen] = useState<boolean | undefined>(undefined);

    const handleClose = () => {
        track("promotion banners", "close banner", "si extension banner");
        setIsBannerOpen(false);
    };

    const handleButtonClick = () => {
        track("promotion banners", "click on cta", "si extension banner");
        setIsBannerOpen(false);
    };

    useEffect(() => {
        if (isBannerVisible) {
            setIsBannerOpen(true);
        }
    }, [isBannerVisible]);

    useEffect(() => {
        if (isBannerOpen === false) {
            setTimeout(() => {
                localStorage.setItem(EXTENSION_BANNER_KEY, "true");
                dispatch(setIsExtensionBannerVisible(false));
            }, 300);
        }
    }, [isBannerOpen]);

    if (!isBannerVisible || !isBannerVisibleClaim) {
        return null;
    }

    return (
        <div className="hidden-print">
            <StyledExtensionBannerOverlay isOpen={isBannerOpen} />
            <StyledExtensionBanner isOpen={isBannerOpen}>
                <StyledExtensionBannerCloseButton
                    type="flat"
                    iconName="clear"
                    onClick={handleClose}
                />
                <StyledExtensionBannerImageBlock>
                    <StyledExtensionBannerImage
                        src={AssetsService.assetUrl("/images/sales-intelligence/extension-promo.png")}
                        alt="SI extension"
                    />
                </StyledExtensionBannerImageBlock>
                <StyledExtensionBannerTextBlock>
                    <StyledExtensionBannerTitle>
                        {translate("si.components.extension_banner.title")}
                    </StyledExtensionBannerTitle>
                    <StyledExtensionBannerDescription>
                        {translate("si.components.extension_banner.description")}
                    </StyledExtensionBannerDescription>
                    <StyledExtensionBannerButtonContainer>
                        <a
                            target="_blank"
                            href={EXTENSION_LINK}
                            rel="noreferrer"
                            onClick={handleButtonClick}
                        >
                            <Button type="outlined">{translate("si.components.extension_banner.button")}</Button>
                        </a>
                    </StyledExtensionBannerButtonContainer>
                </StyledExtensionBannerTextBlock>
            </StyledExtensionBanner>
        </div>
    );
};
